import { apiOnchainFetch } from '../index';

export interface WalletResponse {
  chain: 'sui';
  address: string;
}

export enum SuiWalletProvisioningStatus {
  Ready = 'ready',
  CreationRequired = 'creation_required',
}

export type SuiWalletProvisioningResponse =
  | ({ status: SuiWalletProvisioningStatus.Ready } & WalletResponse)
  | {
      status: SuiWalletProvisioningStatus.CreationRequired;
      attemptId: string;
      capability: string;
      expiresAt: string;
    };

export enum SuiWalletProvisioningStartStatus {
  CreationAuthorized = 'creation_authorized',
}

export interface SuiWalletProvisioningStartResponse {
  status: SuiWalletProvisioningStartStatus.CreationAuthorized;
}

export interface CompleteSuiWalletProvisioningBody {
  capability: string;
  providerWalletId: string;
  address: string;
}

export interface ResolvedRecipient {
  address: string;
  displayName: string | null;
}

export const resolveRecipient = (email: string) =>
  apiOnchainFetch<ResolvedRecipient>('/wallet/recipients/lookup', {
    method: 'POST',
    body: { email },
  });

export const provisionSuiWallet = (privyIdentityToken?: string) =>
  apiOnchainFetch<SuiWalletProvisioningResponse>('/wallet/provisioning', {
    method: 'POST',
    body: { chain: 'sui', ...(privyIdentityToken ? { privyIdentityToken } : {}) },
  });

export const startSuiWalletProvisioning = (attemptId: string, capability: string) =>
  apiOnchainFetch<SuiWalletProvisioningStartResponse>(
    `/wallet/provisioning/${encodeURIComponent(attemptId)}/start`,
    { method: 'POST', body: { capability } },
  );

export const completeSuiWalletProvisioning = (
  attemptId: string,
  body: CompleteSuiWalletProvisioningBody,
) =>
  apiOnchainFetch<
    Extract<SuiWalletProvisioningResponse, { status: SuiWalletProvisioningStatus.Ready }>
  >(`/wallet/provisioning/${encodeURIComponent(attemptId)}/complete`, {
    method: 'POST',
    body,
  });

export enum WalletSyncKind {
  Balances = 'balances',
  History = 'history',
}

export enum WalletSyncStatus {
  Queued = 'queued',
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
}

export interface WalletSyncRunSummary {
  runId: string;
  walletId: string;
  kind: WalletSyncKind;
  status: WalletSyncStatus;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  runExpiresAt: string | null;
  processedPages: number;
  importedTransactions: number;
}

export interface WalletSyncResponse {
  balances: WalletSyncRunSummary;
  history: WalletSyncRunSummary;
}

export enum WalletSyncTrigger {
  TransactionSubmitted = 'transaction_submitted',
}

export interface RequestWalletSyncOptions {
  trigger?: WalletSyncTrigger;
  silent?: boolean;
}

export const requestWalletSync = (options: RequestWalletSyncOptions = {}) =>
  apiOnchainFetch<WalletSyncResponse>('/wallet/syncs', {
    method: 'POST',
    ...(options.trigger ? { body: { trigger: options.trigger } } : {}),
    ...(options.silent ? { onResponseError: () => undefined } : {}),
  });

export enum TransactionIntentType {
  COIN_TRANSFER = 'coin_transfer',
}

export enum TransactionIntentStatus {
  AWAITING_AUTHORIZATION = 'awaiting_authorization',
  SUBMITTING = 'submitting',
  SUBMITTED = 'submitted',
  FAILED = 'failed',
  EXPIRED = 'expired',
}

export enum TransactionGasMode {
  SELF_PAID = 'self_paid',
  SPONSORED = 'sponsored',
}

export enum TransactionSubmissionStatus {
  SUBMITTING = 'submitting',
  SUBMITTED = 'submitted',
  FAILED = 'failed',
}

export interface TransactionIntent {
  intentId: string;
  type: TransactionIntentType;
  status: TransactionIntentStatus;
  walletId?: string;
  transactionBytesBase64?: string;
  gas?: { mode: TransactionGasMode; budget: string };
  expectedHash: string;
  expiresAt: string;
  submissionId?: string;
  digest?: string;
  failureCode?: string;
}

export interface CreateCoinTransferIntentBody {
  type: TransactionIntentType;
  parameters: {
    coinType: string;
    amount: string;
    recipient: string;
  };
  idempotencyKey: string;
}

export const createTransactionIntent = (body: CreateCoinTransferIntentBody) =>
  apiOnchainFetch<TransactionIntent>('/wallet/transaction-intents', { method: 'POST', body });

export interface TransactionSubmission {
  submissionId: string;
  status: TransactionSubmissionStatus;
  digest?: string;
  failureCode?: string;
}

export const submitTransactionIntent = (
  intentId: string,
  body: { authorizationSignature: string },
) =>
  apiOnchainFetch<TransactionSubmission>(`/wallet/transaction-intents/${intentId}/submissions`, {
    method: 'POST',
    body,
  });

export const getTransactionIntent = (intentId: string) =>
  apiOnchainFetch<TransactionIntent>(`/wallet/transaction-intents/${intentId}`);
